<template>
  <div class="flash-swap-modal" v-if="visible" @click="handleOverlayClick">
    <div class="flash-swap-content" @click.stop>
      <!-- 头部 -->
      <div class="modal-header">
        <div class="header-title">
          <img :src="require(`@/assets/flash-${userTheme}.svg`)" width="22" height="22" alt="Flash Swap" class="header-icon" />
          <h3>{{ $t('flashswap.title') }}</h3>
        </div>
        <button class="close-btn" @click="close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>

      <!-- 内容区域 -->
      <div class="modal-body">
        <!-- 用户反馈区域 -->
        <div class="user-feedback" v-if="userFeedback.showLoading || userFeedback.showSuccess || userFeedback.showError">
          <!-- 加载状态 -->
          <div v-if="userFeedback.showLoading" class="feedback-item loading">
            <a-spin :size="16" />
            <span class="feedback-text">{{ userFeedback.loadingText }}</span>
          </div>

          <!-- 成功提示 -->
          <div v-if="userFeedback.showSuccess" class="feedback-item success">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="feedback-text">{{ userFeedback.successMessage }}</span>
          </div>

          <!-- 错误提示 -->
          <div v-if="userFeedback.showError" class="feedback-item error">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span class="feedback-text">{{ userFeedback.errorMessage }}</span>
          </div>
        </div>

        <!-- 兑换卡片 -->
        <div class="swap-card">
          <!-- 支付币种 -->
          <div class="swap-section">
            <div class="section-header">
              <span class="section-title">{{ $t('flashswap.pay') }}</span>
              <div class="balance-info" v-if="payCurrency">
                <span class="balance-label">{{ $t('flashswap.balance') }}:</span>
                <span class="balance-value">{{ payBalance }}</span>
              </div>
            </div>
            <div class="input-section">
              <input
                v-model="payAmount"
                :placeholder="$t('flashswap.enter_amount')"
                class="amount-input pay-amount-input"
                type="text"
                @input="onPayAmountChange"
                @blur="formatPayAmount"
              />
              <div class="currency-selector" @click="showPayCurrencySelector = true">
                <img v-if="payCurrency" :src="payCurrency.icon" class="currency-icon" />
                <span class="currency-symbol">{{ payCurrency?.symbol || $t('flashswap.select_currency') }}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- 兑换箭头 -->
          <div class="swap-arrow" @click="switchCurrencies">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>

          <!-- 汇率信息 -->
          <div class="rate-info" v-if="quoteData && quoteData.to_amount">
            <div class="rate-item">
              <span class="rate-label">{{ $t('flashswap.exchange_rate') }}:</span>
              <span class="rate-value">{{ formattedRate }}</span>
            </div>
            <div class="rate-item">
              <span class="rate-label">{{ $t('flashswap.slippage') }}:</span>
              <span class="rate-value">{{ formattedSlippage }}</span>
            </div>
          </div>

          <!-- 获得币种 -->
          <div class="swap-section">
            <div class="section-header">
              <span class="section-title">{{ $t('flashswap.receive') }}</span>
            </div>
            <div class="input-section">
              <input
                v-model="receiveAmountDisplay"
                :placeholder="$t('flashswap.calculating_amount')"
                class="amount-input receive-amount-input"
                readonly
                type="text"
              />
              <div class="currency-selector" @click="showReceiveCurrencySelector = true">
                <img v-if="receiveCurrency" :src="receiveCurrency.icon" class="currency-icon" />
                <span class="currency-symbol">{{ receiveCurrency?.symbol || $t('flashswap.select_currency') }}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- 错误信息 -->
          <div class="error-message" v-if="errorMessage">
            <span>{{ errorMessage }}</span>
          </div>

          <!-- 操作按钮区域 -->
          <div class="action-buttons">
            <a-button class="history-button" @click="toggleOrderHistory">
              {{ $t('flashswap.order_history') }}
            </a-button>
            <a-button 
              type="primary" 
              class="swap-button" 
              :disabled="!canSwap"
              :loading="loading"
              @click="confirmSwap"
            >
              {{ swapButtonText }}
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 支付币种选择弹窗 -->
    <a-modal
      v-model:visible="showPayCurrencySelector"
      :title="$t('flashswap.select_pay_currency')"
      :footer="null"
      width="400px"
    >
      <div class="currency-list">
        <div 
          v-for="currency in availableCurrencies" 
          :key="currency.symbol"
          class="currency-item"
          @click="selectPayCurrency(currency)"
        >
          <img :src="currency.icon" class="currency-icon" />
          <div class="currency-info">
            <span class="currency-symbol">{{ currency.symbol }}</span>
            <span class="currency-name">{{ currency.name }}</span>
          </div>
          <span class="currency-balance">{{ currency.balance }}</span>
        </div>
      </div>
    </a-modal>

    <!-- 获得币种选择弹窗 -->
    <a-modal
      v-model:visible="showReceiveCurrencySelector"
      :title="$t('flashswap.select_receive_currency')"
      :footer="null"
      width="400px"
    >
      <div class="currency-list">
        <div 
          v-for="currency in availableCurrencies" 
          :key="currency.symbol"
          class="currency-item"
          @click="selectReceiveCurrency(currency)"
        >
          <img :src="currency.icon" class="currency-icon" />
          <div class="currency-info">
            <span class="currency-symbol">{{ currency.symbol }}</span>
            <span class="currency-name">{{ currency.name }}</span>
          </div>
          <span class="currency-balance">{{ currency.balance }}</span>
        </div>
      </div>
    </a-modal>

    <!-- 订单历史弹窗 -->
    <a-modal
      v-model:visible="showOrderHistory"
      :title="$t('flashswap.order_history')"
      :footer="null"
      width="800px"
      :modal-style="{ top: '10vh' }"
      :body-style="{ maxHeight: '70vh', overflowY: 'auto' }"
    >
      <div class="order-list">
        <div v-if="orderHistory.length === 0" class="empty-orders">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M24 4L28 20H44L32 32L36 48L24 36L12 48L16 32L4 20H20L24 4Z" fill="currentColor" opacity="0.3"/>
          </svg>
          <span>{{ $t('flashswap.no_orders') }}</span>
        </div>
        <div v-else class="order-items">
          <div v-for="order in orderHistory" :key="order.id" class="order-item">
            <div class="order-main">
               <div class="order-info">
                 <span class="order-pair">
                   {{ order.from_currency?.symbol || $t('common.error') }} → {{ order.to_currency?.symbol || $t('common.error') }}
                 </span>
               </div>
               <div class="order-details">
                 <div class="detail-row-with-status">
                   <div class="status-above-amount">
                     <span class="order-status" :class="getOrderStatusClass(order.status)">
                       {{ getOrderStatusText(order.status) }}
                     </span>
                   </div>
                   <div class="amount-info">
                     <span class="detail-label">{{ $t('flashswap.amount') }}:</span>
                     <span class="detail-value">{{ formatOrderAmount(order.from_amount, order.from_currency) }} {{ order.from_currency?.symbol || $t('common.error') }}</span>
                   </div>
                 </div>
                <div class="detail-row">
                  <span class="detail-label">{{ $t('flashswap.receive') }}:</span>
                  <span class="detail-value">{{ formatOrderAmount(order.to_amount, order.to_currency) }} {{ order.to_currency?.symbol || $t('common.error') }}</span>
                </div>
              </div>
              <div class="order-time">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1"/>
                  <path d="M6 3V6L8 8" stroke="currentColor" stroke-width="1" stroke-linecap="round"/>
                </svg>
                <span>{{ formatTime(order.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import flashswapApi from '@/common/api/flashswap.js';

export default {
  name: 'FlashSwap',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    balanceList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // 页面状态
      loading: false,
      errorMessage: '',
      
      // 用户反馈状态
      userFeedback: {
        showLoading: false,
        loadingText: '',
        showSuccess: false,
        successMessage: '',
        showError: false,
        errorMessage: ''
      },

      // 币种数据
      availableCurrencies: [],
      availablePairs: [],

      // 当前选择的币种
      payCurrency: null,
      receiveCurrency: null,

      // 兑换数据
      payAmount: null,
      receiveAmountDisplay: '',
      exchangeRate: 0,
      loadingRate: false,
      estimatedFee: 0,
      estimatedSlippage: 0,

      // 报价数据
      quoteData: null,

      // 订单历史
      orderHistory: [],
      showOrderHistory: false,

      // 弹窗状态
      showPayCurrencySelector: false,
      showReceiveCurrencySelector: false
    };
  },
  computed: {
    ...mapGetters(['isLogin', 'userTheme']),

    // 支付余额显示
    payBalance() {
      if (!this.payCurrency?.symbol || !this.balanceList || this.balanceList.length === 0) {
        return '0.000000';
      }
      
      // 从余额列表中查找当前币种的余额
      const balanceItem = this.balanceList.find(item => item.symbol === this.payCurrency.symbol);
      const rawBalance = balanceItem ? balanceItem.balance : '0.000000';
      return this.formatSmartNumber(rawBalance);
    },

    // 是否可以兑换
    canSwap() {
      return this.isLogin && this.payAmount > 0 && this.quoteData && this.quoteData.to_amount && !this.loadingRate;
    },

    // 兑换按钮文本
    swapButtonText() {
      if (!this.isLogin) return this.$t('flashswap.please_login');
      if (this.payAmount <= 0) return this.$t('flashswap.enter_amount');
      if (this.loadingRate) return this.$t('flashswap.calculating_rate');
      if (!this.quoteData || !this.quoteData.to_amount) return this.$t('flashswap.getting_quote');
      return this.$t('flashswap.confirm');
    },

    // 格式化汇率
    formattedRate() {
      let rate = this.exchangeRate;
      if (!rate || rate <= 0) return '0.00';
      if (!this.payCurrency?.symbol || !this.receiveCurrency?.symbol) return '0.00';
      
      const numRate = parseFloat(rate);
      if (isNaN(numRate)) return '0.00';
      
      return `1 ${this.payCurrency.symbol} = ${numRate.toFixed(8)} ${this.receiveCurrency.symbol}`;
    },

    // 格式化滑点
    formattedSlippage() {
      let slippage = this.estimatedSlippage;
      if (!slippage || slippage <= 0) return '0.10%';
      
      const numSlippage = parseFloat(slippage);
      if (isNaN(numSlippage)) return '0.10%';
      
      return `${(numSlippage * 100).toFixed(2)}%`;
    },

    // 格式化获得数量
    formattedReceiveAmount() {
      console.log('formattedReceiveAmount 计算:', {
        quoteData: this.quoteData,
        payAmount: this.payAmount,
        exchangeRate: this.exchangeRate,
        payCurrency: this.payCurrency?.symbol,
        receiveCurrency: this.receiveCurrency?.symbol
      });
      
      if (!this.payAmount || this.payAmount <= 0) {
        console.log('payAmount为空，返回空字符串');
        return '';
      }
      
      let result = '';
      
      // 如果有quoteData且包含to_amount，使用它
      if (this.quoteData && this.quoteData.to_amount !== undefined && this.quoteData.to_amount !== null) {
        console.log('使用quoteData中的to_amount:', this.quoteData.to_amount);
        const numAmount = parseFloat(this.quoteData.to_amount);
        if (!isNaN(numAmount)) {
          if (Number.isInteger(numAmount)) {
            result = numAmount.toString();
          } else {
            result = parseFloat(numAmount.toFixed(8)).toString();
          }
        }
      }
      
      // 如果没有quoteData，但有汇率，直接计算
      if (!result && this.exchangeRate > 0) {
        console.log('使用fallback计算，exchangeRate:', this.exchangeRate);
        const calculatedAmount = parseFloat(this.payAmount) * this.exchangeRate;
        if (Number.isInteger(calculatedAmount)) {
          result = calculatedAmount.toString();
        } else {
          result = parseFloat(calculatedAmount.toFixed(8)).toString();
        }
      }
      
      console.log('formattedReceiveAmount 最终结果:', result, '类型:', typeof result);
      return result;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initData();
      } else {
        this.resetData();
      }
    },
    balanceList: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.initCurrenciesFromBalanceList();
        }
      },
      immediate: true
    }
  },
  methods: {
    // 初始化数据
    async initData() {
      try {
        this.showLoading(this.$t('common.loading'));
        await Promise.all([
          this.loadFlashswapData(),
          this.loadUserBalances(),
          this.loadOrderHistory()
        ]);
        this.setDefaultPair();
        this.hideLoading();
      } catch (error) {
        console.error('初始化数据失败:', error);
        this.showError(this.$t('common.failed'));
      }
    },

    // 从余额列表初始化币种数据
    initCurrenciesFromBalanceList() {
      if (!this.balanceList || this.balanceList.length === 0) return;
      
      const currencies = new Map();
      
      this.balanceList.forEach(balanceItem => {
        if (balanceItem.symbol) {
          const symbol = balanceItem.symbol.toUpperCase().trim();
          currencies.set(symbol, {
            id: balanceItem.id || symbol,
            symbol: symbol,
            name: balanceItem.name || symbol,
            icon: balanceItem.icon || this.getCurrencyIcon(symbol),
            decimals: balanceItem.decimals || 6,
            balance: balanceItem.balance || '0.00'
          });
        }
      });
      
      this.availableCurrencies = Array.from(currencies.values());
      
      // 如果还没有设置默认币种，现在设置
      if (!this.payCurrency && this.availableCurrencies.length > 0) {
        this.setDefaultPair();
      }
    },

    // 重置数据
    resetData() {
      this.payAmount = null;
      this.exchangeRate = 0;
      this.quoteData = null;
      this.errorMessage = '';
      this.userFeedback = {
        showLoading: false,
        loadingText: '',
        showSuccess: false,
        successMessage: '',
        showError: false,
        errorMessage: ''
      };
    },

    // 加载闪兑数据
    async loadFlashswapData() {
      try {
        const pairsResult = await flashswapApi.getPairs();
        if (pairsResult && pairsResult.code === 200 && pairsResult.data) {
          this.availablePairs = pairsResult.data;
          
          // 从交易对中提取币种信息
          const currencies = new Map();
          pairsResult.data.forEach(pair => {
            // 处理from_currency
            if (pair.from_currency && pair.from_currency.symbol) {
              const symbol = pair.from_currency.symbol.toUpperCase().trim();
              if (!currencies.has(symbol)) {
                currencies.set(symbol, {
                  id: pair.from_currency.id || symbol,
                  symbol: symbol,
                  name: pair.from_currency.name || symbol,
                  icon: pair.from_currency.icon || this.getCurrencyIcon(symbol),
                  decimals: pair.from_currency.decimals || 6,
                  balance: '0.00'
                });
              }
            }
            
            // 处理to_currency
            if (pair.to_currency && pair.to_currency.symbol) {
              const symbol = pair.to_currency.symbol.toUpperCase().trim();
              if (!currencies.has(symbol)) {
                currencies.set(symbol, {
                  id: pair.to_currency.id || symbol,
                  symbol: symbol,
                  name: pair.from_currency.name || symbol,
                  icon: pair.to_currency.icon || this.getCurrencyIcon(symbol),
                  decimals: pair.to_currency.decimals || 6,
                  balance: '0.00'
                });
              }
            }
          });
          
          this.availableCurrencies = Array.from(currencies.values());
        }
      } catch (error) {
        console.error('加载闪兑数据失败:', error);
        throw error;
      }
    },

    // 设置默认交易对
    setDefaultPair() {
      console.log('设置默认交易对，可用币种:', this.availableCurrencies);
      
      if (this.availableCurrencies.length >= 2) {
        // 优先设置USDT为支付币种
        const usdtCurrency = this.availableCurrencies.find(c => c.symbol === 'USDT');
        this.payCurrency = usdtCurrency || this.availableCurrencies[0];
        
        // 优先设置BTC为获得币种
        const btcCurrency = this.availableCurrencies.find(c => c.symbol === 'BTC');
        this.receiveCurrency = btcCurrency || this.availableCurrencies[1];
        
        console.log('设置币种:', { payCurrency: this.payCurrency, receiveCurrency: this.receiveCurrency });
      } else {
        // 如果没有可用币种，创建默认币种
        console.log('没有可用币种，创建默认币种');
        this.payCurrency = {
          symbol: 'USDT',
          name: 'Tether',
          icon: this.getCurrencyIcon('USDT'),
          decimals: 6,
          balance: '0.00'
        };
        this.receiveCurrency = {
          symbol: 'BTC',
          name: 'Bitcoin',
          icon: this.getCurrencyIcon('BTC'),
          decimals: 8,
          balance: '0.00'
        };
        console.log('创建默认币种:', { payCurrency: this.payCurrency, receiveCurrency: this.receiveCurrency });
      }
    },

    // 加载用户余额
    async loadUserBalances() {
      try {
        const result = await flashswapApi.getBalances();
        if (result && result.code === 200 && result.data) {
          if (Array.isArray(result.data)) {
            result.data.forEach(currencyBalance => {
              if (currencyBalance.symbol) {
                this.updateCurrencyBalance(currencyBalance.symbol, currencyBalance);
              }
            });
          }
        }
      } catch (error) {
        console.error('加载用户余额失败:', error);
      }
    },

    // 更新币种余额
    updateCurrencyBalance(symbol, balanceData) {
      const currency = this.availableCurrencies.find(c => c.symbol === symbol);
      if (currency) {
        let newBalance = '0.00';
        if (typeof balanceData === 'object') {
          newBalance = balanceData.balance || balanceData.available || balanceData.amount || balanceData.total || '0.00';
        } else if (typeof balanceData === 'string' || typeof balanceData === 'number') {
          newBalance = balanceData.toString();
        }
        currency.balance = newBalance;
        
        // 更新当前选中的币种余额
        if (this.payCurrency && this.payCurrency.symbol === symbol) {
          this.payCurrency.balance = newBalance;
        }
        if (this.receiveCurrency && this.receiveCurrency.symbol === symbol) {
          this.receiveCurrency.balance = newBalance;
        }
      }
    },

    // 加载订单历史
    async loadOrderHistory() {
      try {
        const result = await flashswapApi.getOrders();
        if (result && result.code === 200 && result.data) {
          let ordersData = null;
          if (Array.isArray(result.data)) {
            ordersData = result.data;
          } else if (result.data.list && Array.isArray(result.data.list)) {
            ordersData = result.data.list;
          }
          
          if (ordersData) {
            this.orderHistory = ordersData;
          }
        }
      } catch (error) {
        console.error('加载订单历史失败:', error);
      }
    },

    // 支付数量变化
    onPayAmountChange() {
      console.log('支付数量变化:', this.payAmount);
      console.log('当前币种:', { payCurrency: this.payCurrency, receiveCurrency: this.receiveCurrency });
      
      if (this.payAmount > 0) {
        this.debouncedGetQuote();
      } else {
        // 只清空获得数量，保留汇率
        this.clearReceiveAmount();
      }
    },

    // 格式化支付金额（失去焦点时）
    formatPayAmount() {
      if (this.payAmount && typeof this.payAmount === 'number') {
        // 如果是整数，直接使用整数；否则保留最多8位小数，去掉末尾的0
        if (Number.isInteger(this.payAmount)) {
          this.payAmount = this.payAmount;
        } else {
          const formatted = parseFloat(this.payAmount.toFixed(8));
          this.payAmount = formatted;
        }
      }
    },

    // 计算获得数量（使用默认汇率）
    calculateReceiveAmount() {
      console.log('开始计算获得数量:', {
        payAmount: this.payAmount,
        payCurrency: this.payCurrency,
        receiveCurrency: this.receiveCurrency,
        exchangeRate: this.exchangeRate
      });
      
      if (!this.payAmount || !this.payCurrency || !this.receiveCurrency) {
        console.log('缺少必要参数，无法计算');
        return;
      }
      
      // 设置默认汇率
      if (this.exchangeRate <= 0) {
        if (this.payCurrency.symbol === 'USDT' && this.receiveCurrency.symbol === 'BTC') {
          this.exchangeRate = 0.000023; // USDT到BTC的示例汇率
        } else if (this.payCurrency.symbol === 'BTC' && this.receiveCurrency.symbol === 'USDT') {
          this.exchangeRate = 43000; // BTC到USDT的示例汇率
        } else {
          this.exchangeRate = 1; // 默认汇率
        }
        console.log('设置默认汇率:', this.exchangeRate);
      }
      
      // 计算获得数量
      const calculatedAmount = parseFloat(this.payAmount) * this.exchangeRate;
      
      // 确保quoteData存在
      if (!this.quoteData) {
        this.quoteData = {};
      }
      
      this.quoteData.to_amount = calculatedAmount;
      this.quoteData.rate = this.exchangeRate;
      
      // 更新显示值
      if (Number.isInteger(calculatedAmount)) {
        this.receiveAmountDisplay = calculatedAmount.toString();
      } else {
        this.receiveAmountDisplay = parseFloat(calculatedAmount.toFixed(8)).toString();
      }
      
      console.log('计算完成:', {
        calculatedAmount,
        exchangeRate: this.exchangeRate,
        quoteData: this.quoteData,
        receiveAmountDisplay: this.receiveAmountDisplay
      });
      
      // 强制触发响应式更新
      this.$forceUpdate();
    },

    // 防抖获取报价
    debouncedGetQuote() {
      clearTimeout(this.quoteTimer);
      this.quoteTimer = setTimeout(() => {
        if (this.payAmount > 0 && this.payCurrency && this.receiveCurrency) {
          this.getQuote();
        }
      }, 500);
    },

    // 获取报价
    async getQuote() {
      try {
        this.loadingRate = true;
        
        if (!this.payCurrency?.symbol || !this.receiveCurrency?.symbol || !this.payAmount) {
          throw new Error('缺少必要的参数');
        }

        const quoteData = {
          from_currency: this.payCurrency.symbol,
          to_currency: this.receiveCurrency.symbol,
          amount: this.payAmount
        };

        console.log('获取报价参数:', quoteData);

        // 调用报价API
        const quoteResult = await flashswapApi.getQuote(quoteData);
        console.log('报价API响应:', quoteResult);

        // 处理报价数据
        if (quoteResult && quoteResult.code === 200 && quoteResult.data) {
          this.quoteData = quoteResult.data;
          
          // 从报价API获取汇率 (API返回字段名是exchange_rate)
          if (this.quoteData.exchange_rate !== undefined && this.quoteData.exchange_rate !== null) {
            this.exchangeRate = parseFloat(this.quoteData.exchange_rate);
            console.log('从报价API获取汇率:', this.exchangeRate);
          }
          
          // 从报价API获取获得数量
          if (this.quoteData.to_amount !== undefined && this.quoteData.to_amount !== null) {
            const receivedAmount = parseFloat(this.quoteData.to_amount);
            if (Number.isInteger(receivedAmount)) {
              this.receiveAmountDisplay = receivedAmount.toString();
            } else {
              this.receiveAmountDisplay = parseFloat(receivedAmount.toFixed(8)).toString();
            }
            console.log('从报价API获取获得数量:', this.receiveAmountDisplay);
          }
          
          // 从报价API获取手续费 (API返回字段名是fee_amount)
          if (this.quoteData.fee_amount !== undefined && this.quoteData.fee_amount !== null) {
            this.estimatedFee = parseFloat(this.quoteData.fee_amount);
            console.log('从报价API获取手续费:', this.estimatedFee);
          }
          
          // 从报价API获取滑点
          if (this.quoteData.slippage !== undefined && this.quoteData.slippage !== null) {
            this.estimatedSlippage = parseFloat(this.quoteData.slippage);
            console.log('从报价API获取滑点:', this.estimatedSlippage);
          }
          
          // 如果报价API没有返回获得数量，使用汇率计算
          if (!this.receiveAmountDisplay && this.payAmount && this.exchangeRate > 0) {
            const calculatedAmount = parseFloat(this.payAmount) * this.exchangeRate;
            if (Number.isInteger(calculatedAmount)) {
              this.receiveAmountDisplay = calculatedAmount.toString();
            } else {
              this.receiveAmountDisplay = parseFloat(calculatedAmount.toFixed(8)).toString();
            }
            console.log('使用汇率计算获得数量:', this.receiveAmountDisplay);
          }
        } else {
          console.error('报价API返回错误:', quoteResult);
          this.handleQuoteError(quoteResult);
        }
      } catch (error) {
        console.error('获取报价失败:', error);
        this.resetQuoteData();
        this.showError(this.$t('flashswap.quote_failed'));
      } finally {
        this.loadingRate = false;
      }
    },

    // 处理报价错误
    handleQuoteError(result) {
      if (result && result.code && result.code !== 200) {
        let message = result.message || this.$t('flashswap.quote_failed');
        
        // 处理最低兑换金额不足的错误
        if (result.code === 10004 && result.data && result.data.min_amount) {
          const formattedAmount = this.formatAmount(result.data.min_amount);
          message = this.$t('flashswap.min_amount_error', { amount: formattedAmount });
        }
        // 处理最高兑换金额超限的错误
        else if (result.code === 1001) {
          message = this.$t('flashswap.amount_exceeds_limit');
        }
        // 处理最高兑换金额不足的错误（如果有max_amount字段）
        else if (result.code === 10005 && result.data && result.data.max_amount) {
          const formattedAmount = this.formatAmount(result.data.max_amount);
          message = this.$t('flashswap.max_amount_error', { amount: formattedAmount });
        }
        
        this.showError(message);
      }
    },

    // 处理兑换错误
    handleSwapError(result) {
      if (result && result.code) {
        let message = this.$t('flashswap.swap_failed');
        
        // 处理最低兑换金额不足的错误
        if (result.code === 10004 && result.data && result.data.min_amount) {
          const formattedAmount = this.formatAmount(result.data.min_amount);
          message = this.$t('flashswap.min_amount_error', { amount: formattedAmount });
        }
        // 处理最高兑换金额超限的错误
        else if (result.code === 1001) {
          message = this.$t('flashswap.amount_exceeds_limit');
        }
        // 处理最高兑换金额不足的错误（如果有max_amount字段）
        else if (result.code === 10005 && result.data && result.data.max_amount) {
          const formattedAmount = this.formatAmount(result.data.max_amount);
          message = this.$t('flashswap.max_amount_error', { amount: formattedAmount });
        }
        // 如果API返回了具体的错误信息，优先使用国际化处理
        else if (result.message) {
          // 检查是否是已知的错误信息模式
          if (result.message.includes('最高兑换金额') || result.message.includes('Maximum exchange amount')) {
            // 尝试从错误信息中提取金额
            const amountMatch = result.message.match(/(\d+(?:\.\d+)?)/);
            if (amountMatch) {
              const formattedAmount = this.formatAmount(amountMatch[1]);
              message = this.$t('flashswap.max_amount_error', { amount: formattedAmount });
            } else {
              message = this.$t('flashswap.amount_exceeds_limit');
            }
          } else if (result.message.includes('最低兑换金额') || result.message.includes('Minimum exchange amount')) {
            // 尝试从错误信息中提取金额
            const amountMatch = result.message.match(/(\d+(?:\.\d+)?)/);
            if (amountMatch) {
              const formattedAmount = this.formatAmount(amountMatch[1]);
              message = this.$t('flashswap.min_amount_error', { amount: formattedAmount });
            } else {
              message = this.$t('flashswap.amount_exceeds_limit');
            }
          } else {
            // 其他错误信息，直接使用
            message = result.message;
          }
        }
        
        this.showError(message);
      } else {
        this.showError(this.$t('flashswap.swap_failed'));
      }
    },

    // 重置报价数据
    resetQuoteData() {
      this.exchangeRate = 0;
      this.estimatedFee = 0;
      this.estimatedSlippage = 0;
      this.quoteData = null;
      this.loadingRate = false;
    },

    // 清空获得数量（但保留汇率）
    clearReceiveAmount() {
      if (this.quoteData) {
        this.quoteData.to_amount = null;
      }
      this.receiveAmountDisplay = '';
    },

    // 确认兑换
    async confirmSwap() {
      if (!this.isLogin) {
        this.showError(this.$t('flashswap.please_login'));
        return;
      }

      if (!this.payCurrency || !this.receiveCurrency || !this.payAmount || this.payAmount <= 0) {
        this.showError(this.$t('flashswap.invalid_params'));
        return;
      }

      if (this.payCurrency.symbol === this.receiveCurrency.symbol) {
        this.showError(this.$t('flashswap.same_currency'));
        return;
      }

      try {
        this.loading = true;
        this.showLoading(this.$t('flashswap.processing'));

        const swapData = {
          from_currency: this.payCurrency.symbol,
          to_currency: this.receiveCurrency.symbol,
          amount: parseFloat(this.payAmount)
        };

        const result = await flashswapApi.executeSwap(swapData);
        
        if (result && result.code === 200) {
          this.showSuccess(this.$t('flashswap.success'));
          
          // 清空输入
          this.payAmount = null;
          this.exchangeRate = 0;
          this.quoteData = null;
          
          // 刷新余额和订单
          await this.loadUserBalances();
          await this.loadOrderHistory();
          
          // 通知父组件刷新
          this.$emit('refresh');
        } else {
          // 处理兑换失败的错误
          this.handleSwapError(result);
        }
      } catch (error) {
        console.error('兑换失败:', error);
        this.showError(error.message || this.$t('flashswap.swap_failed'));
      } finally {
        this.loading = false;
        this.hideLoading();
      }
    },

    // 切换币种
    switchCurrencies() {
      const temp = { ...this.payCurrency };
      this.payCurrency = { ...this.receiveCurrency };
      this.receiveCurrency = temp;
      
      // 清空输入和报价
      this.payAmount = null;
      this.exchangeRate = 0;
      this.quoteData = null;
    },

    // 选择支付币种
    selectPayCurrency(currency) {
      if (currency.symbol === this.receiveCurrency?.symbol) {
        this.showError(this.$t('flashswap.same_currency'));
        return;
      }
      
      this.payCurrency = { ...currency };
      this.payAmount = null;
      this.exchangeRate = 0;
      this.quoteData = null;
      this.showPayCurrencySelector = false;
    },

    // 选择获得币种
    selectReceiveCurrency(currency) {
      if (currency.symbol === this.payCurrency?.symbol) {
        this.showError(this.$t('flashswap.same_currency'));
        return;
      }
      
      this.receiveCurrency = { ...currency };
      this.payAmount = null;
      this.exchangeRate = 0;
      this.quoteData = null;
      this.showReceiveCurrencySelector = false;
    },

    // 切换订单历史显示
    toggleOrderHistory() {
      this.showOrderHistory = !this.showOrderHistory;
      if (this.showOrderHistory) {
        this.loadOrderHistory();
      }
    },

    // 获取订单状态文本
    getOrderStatusText(status) {
      const statusMap = {
        0: this.$t('flashswap.status_pending'),
        1: this.$t('flashswap.status_completed'),
        2: this.$t('flashswap.status_cancelled'),
        3: this.$t('flashswap.status_failed'),
        4: this.$t('flashswap.status_processing'),
        5: this.$t('flashswap.status_expired')
      };
      return statusMap[status] || this.$t('flashswap.status_unknown');
    },

    // 获取订单状态样式类
    getOrderStatusClass(status) {
      const classMap = {
        0: 'status-pending',
        1: 'status-completed',
        2: 'status-cancelled',
        3: 'status-failed',
        4: 'status-processing',
        5: 'status-expired'
      };
      return classMap[status] || 'status-unknown';
    },

    // 格式化订单数量
    formatOrderAmount(amount, currency = null) {
      if (!amount || amount === '0' || amount === '0.0' || amount === '0.00') {
        return '0.00';
      }
      
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return '0.00';
      
      // 根据币种类型确定精度
      if (currency) {
        const symbol = currency.symbol || currency;
        
        // BTC相关币种：8位小数，但去除尾随零
        if (symbol === 'BTC' || symbol === 'btc') {
          return this.formatSmartNumber(numAmount, 8);
        }
        // ETH相关币种：6位小数，但去除尾随零
        else if (symbol === 'ETH' || symbol === 'eth') {
          return this.formatSmartNumber(numAmount, 6);
        }
        // USDT等稳定币：2位小数，但去除尾随零
        else if (symbol === 'USDT' || symbol === 'usdt' || 
                 symbol === 'USDC' || symbol === 'usdc' ||
                 symbol === 'BUSD' || symbol === 'busd') {
          return this.formatSmartNumber(numAmount, 2);
        }
        // 其他币种：根据数值大小智能确定精度
        else {
          return this.formatSmartNumber(numAmount, 6);
        }
      }
      
      // 如果没有币种信息，使用智能格式化
      return this.formatSmartNumber(numAmount, 6);
    },

    // 格式化时间
    formatTime(timeString) {
      if (!timeString) return this.$t('common.not_available');
      
      try {
        const timestamp = typeof timeString === 'number' ? timeString * 1000 : timeString;
        const date = new Date(timestamp);
        
        if (isNaN(date.getTime())) {
          return this.$t('common.not_available');
        }
        
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      } catch (error) {
        console.error('时间格式化失败:', error);
        return this.$t('common.not_available');
      }
    },

    // 智能格式化数字
    formatSmartNumber(value) {
      if (!value && value !== 0) return '0';
      
      const strValue = String(value);
      if (strValue.includes('.')) {
        const [integerPart, decimalPart] = strValue.split('.');
        if (decimalPart === '0' || /^0+$/.test(decimalPart)) {
          return integerPart;
        }
        const trimmedDecimal = decimalPart.replace(/0+$/, '');
        if (trimmedDecimal === '') {
          return integerPart;
        }
        return `${integerPart}.${trimmedDecimal}`;
      }
      
      if (Number.isInteger(Number(value))) {
        return String(Number(value));
      }
      
      return strValue;
    },

    // 格式化金额（用于错误提示）
    formatAmount(amount) {
      if (!amount) return '0';
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return '0';
      
      // 如果金额很大，使用千分位分隔符而不是科学计数法
      if (numAmount >= 1000000) {
        return numAmount.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        });
      }
      
      // 如果金额很小，保留更多小数位
      if (numAmount < 0.000001) {
        return numAmount.toFixed(8);
      }
      
      // 普通金额，保留2位小数
      return numAmount.toFixed(2);
    },

    // 获取币种图标
    getCurrencyIcon(symbol) {
      if (!symbol) return require('@/assets/images/coin/btc.png');
      try {
        return require(`@/assets/images/coin/${symbol.toLowerCase()}.png`);
      } catch (error) {
        // 如果找不到对应币种图标，返回默认图标
        return require('@/assets/images/coin/btc.png');
      }
    },

    // 显示加载状态
    showLoading(text = this.$t('common.loading')) {
      this.userFeedback.showLoading = true;
      this.userFeedback.loadingText = text;
    },

    // 隐藏加载状态
    hideLoading() {
      this.userFeedback.showLoading = false;
      this.userFeedback.loadingText = '';
    },

    // 显示成功提示
    showSuccess(message) {
      this.userFeedback.showSuccess = true;
      this.userFeedback.successMessage = message;
      setTimeout(() => {
        this.userFeedback.showSuccess = false;
      }, 3000);
    },

    // 显示错误提示
    showError(message) {
      this.userFeedback.showError = true;
      this.userFeedback.errorMessage = message;
      setTimeout(() => {
        this.userFeedback.showError = false;
      }, 5000);
    },

    // 关闭弹窗
    close() {
      this.$emit('close');
    },

    // 处理遮罩点击
    handleOverlayClick() {
      this.close();
    }
  },
  beforeUnmount() {
    if (this.quoteTimer) {
      clearTimeout(this.quoteTimer);
    }
  }
};
</script>

<style lang="scss" scoped>
.flash-swap-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.flash-swap-content {
  background: var(--background-primary);
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-secondary);

  .header-title {
    display: flex;
    align-items: center;
    gap: 8px;

    .header-icon {
      flex-shrink: 0;
    }

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: var(--content-primary);
    }
  }

  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    color: var(--content-secondary);
    transition: all 0.2s ease;

    &:hover {
      background: var(--background-tertiary);
      color: var(--content-primary);
    }
  }
}

.modal-body {
  padding: 24px;
  max-height: calc(90vh - 80px);
  overflow-y: auto;
}

.user-feedback {
  margin-bottom: 20px;

  .feedback-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;

    &.loading {
      background: rgba(var(--color-primary-rgb), 0.1);
      border: 1px solid rgba(var(--color-primary-rgb), 0.3);
      color: var(--color-primary);
    }

    &.success {
      background: rgba(var(--color-success-rgb), 0.1);
      border: 1px solid rgba(var(--color-success-rgb), 0.3);
      color: var(--color-success);
    }

    &.error {
      background: rgba(var(--color-error-rgb), 0.1);
      border: 1px solid rgba(var(--color-error-rgb), 0.3);
      color: var(--color-error);
    }
  }
}

.swap-card {
  .swap-section {
    margin-bottom: 20px;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .section-title {
        font-size: 14px;
        font-weight: 500;
        color: var(--content-primary);
      }

      .balance-info {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;

        .balance-label {
          color: var(--content-primary);
        }

        .balance-value {
          color: var(--color-success);
          font-weight: 600;
        }
      }
    }

    .input-section {
      display: flex;
      gap: 12px;

      .amount-input {
        flex: 1;
      }

      .pay-amount-input,
      .receive-amount-input {
        color: var(--content-primary) !important;
        background-color: transparent !important;
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        
        &::placeholder {
          color: var(--content-secondary) !important;
        }
        
        &:focus {
          color: var(--content-primary) !important;
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
      }

      .currency-selector {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        border: 1px solid var(--border-color);
        border-radius: 6px;
        background: var(--background-secondary);
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 120px;

        &:hover {
          border-color: var(--color-primary);
        }

        .currency-icon {
          width: 20px;
          height: 20px;
          border-radius: 50%;
        }

        .currency-symbol {
          font-size: 14px;
          font-weight: 600;
          color: var(--content-primary);
        }
      }
    }
  }

  .swap-arrow {
    display: flex;
    justify-content: center;
    margin: 16px 0;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  .rate-info {
    background: var(--background-tertiary);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;

    .rate-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      font-size: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      .rate-label {
        color: var(--content-primary);
      }

      .rate-value {
        color: var(--color-primary);
        font-weight: 500;
      }
    }
  }

  .error-message {
    background: var(--color-error);
    color: white;
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 20px;
    text-align: center;
    font-size: 14px;
  }

  .action-buttons {
    display: flex;
    gap: 12px;
    margin-top: 24px;

    .history-button {
      flex: 1;
      height: 40px;
      border: 1px solid var(--color-primary);
      background: transparent;
      color: var(--color-primary);
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background: var(--color-primary);
        color: white;
      }
    }

    .swap-button {
      flex: 2;
      height: 40px;
      border-radius: 6px;
    }
  }
}

.currency-list {
  max-height: 400px;
  overflow-y: auto;

  .currency-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.2s ease;

    &:hover {
      background: var(--background-secondary);
    }

    .currency-icon {
      width: 32px;
      height: 32px;
      border-radius: 50%;
    }

    .currency-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .currency-symbol {
        font-size: 14px;
        font-weight: 600;
        color: var(--content-primary);
      }

      .currency-name {
        font-size: 12px;
        color: var(--content-primary);
      }
    }

    .currency-balance {
      font-size: 12px;
      color: var(--content-primary);
    }
  }
}

.order-list {
  .empty-orders {
    text-align: center;
    padding: 40px 20px;
    color: var(--content-primary);

    svg {
      margin-bottom: 12px;
    }
  }

  .order-items {
    .order-item {
      padding: 12px 16px;
      border-bottom: 1px solid var(--border-color);
      cursor: pointer;
      transition: all 0.2s ease;
      border-radius: 8px;
      margin-bottom: 8px;

      &:hover {
        background: var(--background-secondary);
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      &:last-child {
        border-bottom: none;
        margin-bottom: 0;
      }

      .order-main {
         .order-info {
           display: flex;
           justify-content: space-between;
           align-items: flex-start;
           margin-bottom: 8px;
           gap: 12px;

           .order-pair {
             font-size: 14px;
             font-weight: 600;
             color: var(--content-primary);
             flex: 1;
             min-width: 0;
           }

        }

        .order-details {
          margin-bottom: 8px;
          background: var(--background-tertiary);
          padding: 8px 12px;
          border-radius: 6px;

           .detail-row {
             display: flex;
             justify-content: space-between;
             align-items: center;
             margin-bottom: 4px;
             font-size: 13px;

             &:last-child {
               margin-bottom: 0;
             }

             .detail-label {
               color: var(--content-primary);
               font-size: 12px;
             }

             .detail-value {
               color: var(--content-primary);
               font-weight: 600;
               font-size: 13px;
             }
           }

           .detail-row-with-status {
             display: flex;
             flex-direction: column;
             margin-bottom: 4px;
             font-size: 13px;
             position: relative;

             .status-above-amount {
               position: absolute;
               top: -42px;
               right: 0;
               z-index: 1;

               .order-status {
                 padding: 3px 6px;
                 border-radius: 4px;
                 font-size: 11px;
                 font-weight: 600;
                 white-space: nowrap;
                 flex-shrink: 0;
                 width: fit-content;
                 display: inline-block;
                 letter-spacing: 0;
                 line-height: 1.2;
                 box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

                 &.status-pending {
                   background: #ff9500;
                   color: white;
                 }

                 &.status-completed {
                   background: #34c759;
                   color: white;
                 }

                 &.status-cancelled {
                   background: #8e8e93;
                   color: white;
                 }

                 &.status-failed {
                   background: #ff3b30;
                   color: white;
                 }

                 &.status-processing {
                   background: #007aff;
                   color: white;
                 }

                 &.status-expired {
                   background: #c7c7cc;
                   color: white;
                 }
               }
             }

             .amount-info {
               display: flex;
               justify-content: space-between;
               align-items: center;
               margin-top: 8px;

               .detail-label {
                 color: var(--content-primary);
                 font-size: 12px;
               }

               .detail-value {
                 color: var(--content-primary);
                 font-weight: 600;
                 font-size: 13px;
               }
             }
           }
        }

        .order-time {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: var(--content-primary);
        }
      }
    }
  }
}
</style>
